import type { OrderRow } from "../../../types";
import { parseOrderCreatedAt } from "./ordersPeriodDefaults";

export type OrdersTableFilterOptions = {
    contextualCustomerName: string;
    normalizedSearch: string;
    statusFilter: string;
    channelFilter: string;
    dateFrom: string;
    dateTo: string;
    traderFilter: string;
    regionFilter: string;
};

function matchesPeriod(row: OrderRow, dateFrom: string, dateTo: string) {
    if (dateFrom === "" && dateTo === "") {
        return true;
    }

    const created = parseOrderCreatedAt(row.createdAt);

    if (!created) {
        return true;
    }

    const day = created.toISOString().slice(0, 10);

    if (dateFrom !== "" && day < dateFrom) {
        return false;
    }

    if (dateTo !== "" && day > dateTo) {
        return false;
    }

    return true;
}

function matchesTrader(row: OrderRow, traderFilter: string) {
    if (traderFilter === "" || traderFilter === "Всички") {
        return true;
    }

    const dealerId = row.dealerId ?? 0;

    return String(dealerId) === traderFilter;
}

function matchesRegion(row: OrderRow, regionFilter: string) {
    if (regionFilter === "" || regionFilter === "Всички") {
        return true;
    }

    const region = (row.region ?? row.deliveryRoute ?? "").trim();

    return region === regionFilter;
}

export function findWorkflowOrderMatch(
    rows: OrderRow[],
    options: {
        selectedOrderNumber?: string;
        normalizedSearch?: string;
    },
): OrderRow | null {
    const selectedOrderNumber = (options.selectedOrderNumber ?? "").trim();
    const normalizedSearch = (options.normalizedSearch ?? "").trim().toLowerCase();

    if (selectedOrderNumber !== "") {
        const exact = rows.find((row) => row.orderNumber === selectedOrderNumber);
        if (exact) {
            return exact;
        }

        const numericOrderId = Number.parseInt(selectedOrderNumber, 10);
        if (Number.isFinite(numericOrderId) && numericOrderId > 0) {
            const byOrderId = rows.find((row) => row.orderId === numericOrderId);
            if (byOrderId) {
                return byOrderId;
            }
        }

        const needle = selectedOrderNumber.toLowerCase();
        const partial = rows.find((row) =>
            row.orderNumber.toLowerCase().includes(needle)
            || String(row.orderId ?? "").includes(needle),
        );
        if (partial) {
            return partial;
        }
    }

    if (normalizedSearch === "") {
        return null;
    }

    const matches = rows.filter((row) =>
        [row.orderNumber, row.customer, row.location, String(row.orderId ?? "")]
            .join(" ")
            .toLowerCase()
            .includes(normalizedSearch),
    );

    if (matches.length === 0) {
        return null;
    }

    const numericSearch = Number.parseInt(normalizedSearch, 10);
    if (Number.isFinite(numericSearch) && numericSearch > 0) {
        return matches.find((row) => row.orderId === numericSearch) ?? matches[0];
    }

    return matches[0];
}

export function normalizeCustomerValue(value: string) {
    return value.trim().toLocaleLowerCase("bg-BG");
}

export function filterOrdersTableRows(rows: OrderRow[], options: OrdersTableFilterOptions) {
    const {
        contextualCustomerName,
        normalizedSearch,
        statusFilter,
        channelFilter,
        dateFrom,
        dateTo,
        traderFilter,
        regionFilter,
    } = options;

    return rows.filter((row) => {
        const matchesContext =
            contextualCustomerName.length === 0 ||
            normalizeCustomerValue(row.customer) === contextualCustomerName;
        const matchesSearch =
            normalizedSearch.length === 0 ||
            [row.orderNumber, row.customer, row.location, row.campaign, row.channel, row.sync]
                .join(" ")
                .toLowerCase()
                .includes(normalizedSearch);
        const matchesStatus = statusFilter === "Всички" || row.status === statusFilter;
        const matchesChannel = channelFilter === "Всички" || row.channel === channelFilter;

        return (
            matchesContext
            && matchesSearch
            && matchesStatus
            && matchesChannel
            && matchesPeriod(row, dateFrom, dateTo)
            && matchesTrader(row, traderFilter)
            && matchesRegion(row, regionFilter)
        );
    });
}

export function filterWorkflowTableRows(
    rows: OrderRow[],
    options: Pick<
        OrdersTableFilterOptions,
        | "contextualCustomerName"
        | "normalizedSearch"
        | "channelFilter"
        | "dateFrom"
        | "dateTo"
        | "traderFilter"
        | "regionFilter"
    >,
) {
    const {
        contextualCustomerName,
        normalizedSearch,
        channelFilter,
        dateFrom,
        dateTo,
        traderFilter,
        regionFilter,
    } = options;

    return rows.filter((row) => {
        const matchesContext =
            contextualCustomerName.length === 0 ||
            normalizeCustomerValue(row.customer) === contextualCustomerName;
        const matchesSearch =
            normalizedSearch.length === 0 ||
            [
                row.orderNumber,
                row.customer,
                row.location,
                row.deliveryRoute,
                row.sync,
                row.campaign,
                row.dealerName ?? "",
            ]
                .join(" ")
                .toLowerCase()
                .includes(normalizedSearch);
        const matchesChannel = channelFilter === "Всички" || row.channel === channelFilter;

        return (
            matchesContext
            && matchesSearch
            && matchesChannel
            && matchesPeriod(row, dateFrom, dateTo)
            && matchesTrader(row, traderFilter)
            && matchesRegion(row, regionFilter)
        );
    });
}

export function filterWorkflowBoardRows(
    rows: OrderRow[],
    options: Pick<
        OrdersTableFilterOptions,
        "contextualCustomerName" | "channelFilter" | "dateFrom" | "dateTo" | "traderFilter" | "regionFilter"
    >,
) {
    const { contextualCustomerName, channelFilter, dateFrom, dateTo, traderFilter, regionFilter } = options;

    return rows.filter((row) => {
        const matchesContext =
            contextualCustomerName.length === 0 ||
            normalizeCustomerValue(row.customer) === contextualCustomerName;
        const matchesChannel = channelFilter === "Всички" || row.channel === channelFilter;

        return (
            matchesContext
            && matchesChannel
            && matchesPeriod(row, dateFrom, dateTo)
            && matchesTrader(row, traderFilter)
            && matchesRegion(row, regionFilter)
        );
    });
}
